import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Settings2,
  AlertCircle,
} from 'lucide-react';
import type { CheckoutFieldConfig } from '../../../../types/funnel.types';
import { useConfirmDialog } from '../../../../contexts/ConfirmDialogContext';

// ─── Default WooCommerce Checkout Fields ─────────────────────────────────────

const DEFAULT_WC_FIELDS: CheckoutFieldConfig[] = [
  // Billing section
  { id: 'billing_heading', label: 'Dane rozliczeniowe', type: 'heading', section: 'billing', required: false, enabled: true, wcKey: '_billing_heading' },
  { id: 'billing_first_name', label: 'Imię', type: 'text', section: 'billing', required: true, enabled: true, wcKey: 'billing_first_name', placeholder: '' },
  { id: 'billing_last_name', label: 'Nazwisko', type: 'text', section: 'billing', required: true, enabled: true, wcKey: 'billing_last_name', placeholder: '' },
  { id: 'billing_company', label: 'Firma (opcjonalnie)', type: 'text', section: 'billing', required: false, enabled: true, wcKey: 'billing_company', placeholder: '' },
  { id: 'billing_country', label: 'Kraj / region', type: 'country', section: 'billing', required: true, enabled: true, wcKey: 'billing_country' },
  { id: 'billing_address_1', label: 'Ulica', type: 'text', section: 'billing', required: true, enabled: true, wcKey: 'billing_address_1', placeholder: 'Nazwa ulicy i numer domu' },
  { id: 'billing_address_2', label: 'Linia adresu 2', type: 'text', section: 'billing', required: false, enabled: true, wcKey: 'billing_address_2', placeholder: 'Mieszkanie, lokal itp. (opcjonalnie)' },
  { id: 'billing_city', label: 'Miasto', type: 'text', section: 'billing', required: true, enabled: true, wcKey: 'billing_city', placeholder: '' },
  { id: 'billing_state', label: 'Województwo', type: 'state', section: 'billing', required: false, enabled: true, wcKey: 'billing_state' },
  { id: 'billing_postcode', label: 'Kod pocztowy', type: 'text', section: 'billing', required: true, enabled: true, wcKey: 'billing_postcode', placeholder: '' },
  { id: 'billing_phone', label: 'Telefon', type: 'tel', section: 'billing', required: true, enabled: true, wcKey: 'billing_phone', placeholder: '' },
  { id: 'billing_email', label: 'Adres e-mail', type: 'email', section: 'billing', required: true, enabled: true, wcKey: 'billing_email', placeholder: '' },

  // Shipping section
  { id: 'shipping_heading', label: 'Adres wysyłki', type: 'heading', section: 'shipping', required: false, enabled: true, wcKey: '_shipping_heading' },
  { id: 'shipping_first_name', label: 'Imię', type: 'text', section: 'shipping', required: true, enabled: true, wcKey: 'shipping_first_name', placeholder: '' },
  { id: 'shipping_last_name', label: 'Nazwisko', type: 'text', section: 'shipping', required: true, enabled: true, wcKey: 'shipping_last_name', placeholder: '' },
  { id: 'shipping_company', label: 'Firma (opcjonalnie)', type: 'text', section: 'shipping', required: false, enabled: true, wcKey: 'shipping_company', placeholder: '' },
  { id: 'shipping_country', label: 'Kraj / region', type: 'country', section: 'shipping', required: true, enabled: true, wcKey: 'shipping_country' },
  { id: 'shipping_address_1', label: 'Ulica', type: 'text', section: 'shipping', required: true, enabled: true, wcKey: 'shipping_address_1', placeholder: 'Nazwa ulicy i numer domu' },
  { id: 'shipping_address_2', label: 'Linia adresu 2', type: 'text', section: 'shipping', required: false, enabled: true, wcKey: 'shipping_address_2', placeholder: 'Mieszkanie, lokal itp. (opcjonalnie)' },
  { id: 'shipping_city', label: 'Miasto', type: 'text', section: 'shipping', required: true, enabled: true, wcKey: 'shipping_city', placeholder: '' },
  { id: 'shipping_state', label: 'Województwo', type: 'state', section: 'shipping', required: false, enabled: true, wcKey: 'shipping_state' },
  { id: 'shipping_postcode', label: 'Kod pocztowy', type: 'text', section: 'shipping', required: true, enabled: true, wcKey: 'shipping_postcode', placeholder: '' },

  // Order section
  { id: 'order_heading', label: 'Informacje dodatkowe', type: 'heading', section: 'order', required: false, enabled: true, wcKey: '_order_heading' },
  { id: 'order_comments', label: 'Uwagi do zamówienia', type: 'textarea', section: 'order', required: false, enabled: true, wcKey: 'order_comments', placeholder: 'Uwagi dotyczące zamówienia, np. specjalne informacje odnośnie dostawy.' },
];

// ─── Section labels & colors ─────────────────────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
  billing: 'Rozliczenie',
  shipping: 'Wysyłka',
  order: 'Zamówienie',
  custom: 'Niestandardowe',
};

const SECTION_COLORS: Record<string, string> = {
  billing: 'text-accent-info bg-info/10',
  shipping: 'text-purple-400 bg-purple-500/10',
  order: 'text-amber-400 bg-amber-500/10',
  custom: 'text-accent-primary bg-accent-primary/10',
};

const CHECKOUT_SECTION_ORDER: ('billing' | 'shipping' | 'order' | 'custom')[] = ['billing', 'shipping', 'order', 'custom'];

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Tekst',
  email: 'E-mail',
  tel: 'Telefon',
  textarea: 'Wieloliniowy',
  select: 'Lista wyboru',
  checkbox: 'Checkbox',
  country: 'Kraj',
  state: 'Województwo',
  heading: 'Nagłówek',
};

const CONDITION_OPERATORS: { value: string; label: string }[] = [
  { value: 'equals', label: 'równe' },
  { value: 'not_equals', label: 'różne od' },
  { value: 'contains', label: 'zawiera' },
  { value: 'not_empty', label: 'niepuste' },
  { value: 'empty', label: 'puste' },
];

const FIELD_WIDTH_OPTIONS = [
  { value: 'full', label: '100%' },
  { value: 'half', label: '50%' },
] as const;

const DEFAULT_DESKTOP_HALF_FIELDS = new Set([
  'billing_first_name',
  'billing_last_name',
  'shipping_first_name',
  'shipping_last_name',
]);

function getDesktopWidth(field: CheckoutFieldConfig): 'full' | 'half' {
  if (field.widthDesktop === 'half' || field.widthDesktop === 'full') return field.widthDesktop;
  if (field.cssClass === 'form-row-half') return 'half';
  if (DEFAULT_DESKTOP_HALF_FIELDS.has(field.id)) return 'half';
  return 'full';
}

function getMobileWidth(field: CheckoutFieldConfig): 'full' | 'half' {
  if (field.widthMobile === 'half' || field.widthMobile === 'full') return field.widthMobile;
  return 'full';
}

function isLockedPhoneField(field: CheckoutFieldConfig): boolean {
  return field.wcKey === 'billing_phone' || field.id === 'billing_phone';
}

function normalizeCheckoutField(field: CheckoutFieldConfig): CheckoutFieldConfig {
  if (!isLockedPhoneField(field)) return field;
  return { ...field, enabled: true, required: true, type: 'tel' };
}

function normalizeCheckoutFields(fields: CheckoutFieldConfig[]): CheckoutFieldConfig[] {
  return fields.map(normalizeCheckoutField);
}

// ─── SortableFieldItem ──────────────────────────────────────────────────────

function SortableFieldItem({
  field,
  allFields,
  expandedId,
  onToggleExpand,
  onUpdate,
  onRemove,
}: {
  field: CheckoutFieldConfig;
  allFields: CheckoutFieldConfig[];
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  onUpdate: (id: string, updates: Partial<CheckoutFieldConfig>) => void;
  onRemove: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isExpanded = expandedId === field.id;
  const isHeading = field.type === 'heading';
  const desktopWidth = getDesktopWidth(field);
  const mobileWidth = getMobileWidth(field);
  const lockedPhoneField = isLockedPhoneField(field);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border overflow-hidden transition-colors ${
        isDragging ? 'border-accent-primary/30 bg-surface-hover/80' :
        !field.enabled ? 'border-border-subtle bg-surface-raised/30' :
        isHeading ? 'border-border bg-surface-hover/50' :
        'border-border-subtle bg-surface-raised/50'
      }`}
    >
      {/* Compact row */}
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-0.5 text-text-disabled hover:text-text-secondary cursor-grab active:cursor-grabbing shrink-0"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>

        {/* Section badge */}
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${SECTION_COLORS[field.section]}`}>
          {SECTION_LABELS[field.section]}
        </span>

        {/* Label */}
        <button
          onClick={() => onToggleExpand(field.id)}
          className="flex items-center gap-1 flex-1 min-w-0 text-left"
        >
          {isExpanded
            ? <ChevronDown className="w-3 h-3 text-text-secondary shrink-0" />
            : <ChevronRight className="w-3 h-3 text-text-secondary shrink-0" />}
          <span className={`text-xs truncate ${
            isHeading ? 'font-semibold text-text-primary' :
            field.enabled ? 'text-text-secondary' : 'text-text-muted line-through'
          }`}>
            {field.label}
          </span>
          {isHeading && <span className="text-[9px] text-text-muted ml-1">(nagłówek)</span>}
        </button>

        {/* Type badge */}
        {!isHeading && (
          <span className="text-[9px] text-text-muted shrink-0">
            {FIELD_TYPE_LABELS[field.type]}
          </span>
        )}

        {/* Required indicator */}
        {field.required && field.enabled && !isHeading && (
          <span className="text-[9px] text-danger shrink-0">*</span>
        )}

        {/* Condition indicator */}
        {field.condition && (
          <span className="shrink-0" title="Ma warunek wyświetlania">
            <AlertCircle className="w-3 h-3 text-amber-400" />
          </span>
        )}

        {/* Toggle visibility */}
        <button
          onClick={() => !lockedPhoneField && onUpdate(field.id, { enabled: !field.enabled })}
          disabled={lockedPhoneField}
          className={`p-0.5 shrink-0 transition-colors ${
            field.enabled ? 'text-success hover:text-success/70' : 'text-text-disabled hover:text-text-secondary'
          } ${lockedPhoneField ? 'opacity-60 cursor-not-allowed' : ''}`}
          title={lockedPhoneField ? 'Telefon jest wymagany dla logistyki' : field.enabled ? 'Ukryj pole' : 'Pokaż pole'}
        >
          {field.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>

        {/* Delete (only custom fields) */}
        {field.isCustom && (
          <button
            onClick={() => onRemove(field.id)}
            className="p-0.5 text-text-disabled hover:text-danger shrink-0"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Expanded config */}
      {isExpanded && (
        <div className="border-t border-border-subtle px-3 py-2.5 space-y-2.5">
          {/* Label */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-text-secondary mb-0.5">Etykieta</label>
              <input
                type="text"
                value={field.label}
                onChange={e => onUpdate(field.id, { label: e.target.value })}
                className="w-full px-2.5 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-primary focus:outline-none focus:border-accent-primary/50"
              />
            </div>
            {!isHeading && (
              <div>
                <label className="block text-[10px] text-text-secondary mb-0.5">Placeholder</label>
                <input
                  type="text"
                  value={field.placeholder ?? ''}
                  onChange={e => onUpdate(field.id, { placeholder: e.target.value || undefined })}
                  className="w-full px-2.5 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-primary focus:outline-none focus:border-accent-primary/50"
                  placeholder="Tekst zastępczy..."
                />
              </div>
            )}
          </div>

          {!isHeading && (
            <>
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input type="checkbox" checked={lockedPhoneField || (field.showForPickup ?? !['billing_company', 'billing_country', 'billing_address_1', 'billing_address_2', 'billing_city', 'billing_state', 'billing_postcode'].includes(field.wcKey ?? field.id))}
                  disabled={!field.enabled || lockedPhoneField}
                  onChange={e => onUpdate(field.id, { showForPickup: e.target.checked })} />
                Pokaż przy odbiorze w punkcie
              </label>
              <p className="text-xs text-text-muted">Dotyczy uproszczonego formularza. Faktura i wymagania płatności zachowują potrzebne pola. Telefon jest wymagany dla dostawy. Wyłączone pole pozostaje wyłączone.</p>
              {/* Type & required */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-text-secondary mb-0.5">Typ pola</label>
                  <select
                    value={field.type}
                    onChange={e => onUpdate(field.id, { type: e.target.value as CheckoutFieldConfig['type'] })}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-primary focus:outline-none focus:border-accent-primary/50"
                  >
                    <option value="text">Tekst</option>
                    <option value="email">E-mail</option>
                    <option value="tel">Telefon</option>
                    <option value="textarea">Wieloliniowy</option>
                    <option value="select">Lista wyboru</option>
                    <option value="checkbox">Checkbox</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-text-secondary mb-0.5">Desktop</label>
                    <div className="flex gap-1">
                      {FIELD_WIDTH_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => onUpdate(field.id, {
                            widthDesktop: opt.value,
                            cssClass: opt.value === 'half' ? 'form-row-half' : 'form-row-wide',
                          })}
                          className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-medium border transition-colors ${
                            desktopWidth === opt.value
                              ? 'bg-accent-primary/10 border-accent-primary/30 text-accent-primary'
                              : 'bg-surface-hover border-border text-text-secondary hover:text-text-primary hover:border-border-strong'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-text-secondary mb-0.5">Mobile</label>
                    <div className="flex gap-1">
                      {FIELD_WIDTH_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => onUpdate(field.id, {
                            widthDesktop: desktopWidth,
                            widthMobile: opt.value,
                            cssClass: desktopWidth === 'half' ? 'form-row-half' : 'form-row-wide',
                          })}
                          className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-medium border transition-colors ${
                            mobileWidth === opt.value
                              ? 'bg-accent-primary/10 border-accent-primary/30 text-accent-primary'
                              : 'bg-surface-hover border-border text-text-secondary hover:text-text-primary hover:border-border-strong'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Options for select */}
              {field.type === 'select' && (
                <div>
                  <label className="block text-[10px] text-text-secondary mb-0.5">Opcje (po jednej w linii)</label>
                  <textarea
                    value={(field.options ?? []).join('\n')}
                    onChange={e => onUpdate(field.id, { options: e.target.value.split('\n').filter(Boolean) })}
                    rows={3}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-primary focus:outline-none focus:border-accent-primary/50 resize-none font-mono"
                    placeholder="Opcja 1&#10;Opcja 2"
                  />
                </div>
              )}

              {/* Default value */}
              <div>
                <label className="block text-[10px] text-text-secondary mb-0.5">Wartość domyślna</label>
                <input
                  type="text"
                  value={field.defaultValue ?? ''}
                  onChange={e => onUpdate(field.id, { defaultValue: e.target.value || undefined })}
                  className="w-full px-2.5 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-primary focus:outline-none focus:border-accent-primary/50"
                />
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.required}
                    disabled={lockedPhoneField}
                    onChange={e => !lockedPhoneField && onUpdate(field.id, { required: e.target.checked })}
                    className="w-3.5 h-3.5 rounded bg-surface-hover border-border-strong text-accent-primary focus:ring-accent-primary/30"
                  />
                  <span className="text-[10px] text-text-secondary">Wymagane</span>
                </label>
                {lockedPhoneField && (
                  <span className="text-[10px] text-text-muted">Wymagane dla wysyłek</span>
                )}
              </div>

              {/* Conditional logic */}
              <div className="p-2.5 rounded-lg border border-border-subtle space-y-2">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!field.condition}
                    onChange={e => {
                      if (e.target.checked) {
                        onUpdate(field.id, {
                          condition: { fieldId: '', operator: 'not_empty' },
                        });
                      } else {
                        onUpdate(field.id, { condition: undefined });
                      }
                    }}
                    className="w-3.5 h-3.5 rounded bg-surface-hover border-border-strong text-accent-primary focus:ring-accent-primary/30"
                  />
                  <span className="text-[10px] text-text-secondary">Warunkowe wyświetlanie</span>
                </label>

                {field.condition && (
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    <div>
                      <label className="block text-[9px] text-text-muted mb-0.5">Gdy pole</label>
                      <select
                        value={field.condition.fieldId}
                        onChange={e => onUpdate(field.id, {
                          condition: { ...field.condition!, fieldId: e.target.value },
                        })}
                        className="w-full px-2 py-1 rounded-lg bg-surface-hover border border-border text-[10px] text-text-primary"
                      >
                        <option value="">-- Wybierz --</option>
                        {allFields
                          .filter(f => f.id !== field.id && f.type !== 'heading')
                          .map(f => (
                            <option key={f.id} value={f.id}>{f.label}</option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[9px] text-text-muted mb-0.5">Operator</label>
                      <select
                        value={field.condition.operator}
                        onChange={e => onUpdate(field.id, {
                          condition: { ...field.condition!, operator: e.target.value as CheckoutFieldConfig['condition'] extends undefined ? never : NonNullable<CheckoutFieldConfig['condition']>['operator'] },
                        })}
                        className="w-full px-2 py-1 rounded-lg bg-surface-hover border border-border text-[10px] text-text-primary"
                      >
                        {CONDITION_OPERATORS.map(op => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                    </div>
                    {!['not_empty', 'empty'].includes(field.condition.operator) && (
                      <div>
                        <label className="block text-[9px] text-text-muted mb-0.5">Wartość</label>
                        <input
                          type="text"
                          value={field.condition.value ?? ''}
                          onChange={e => onUpdate(field.id, {
                            condition: { ...field.condition!, value: e.target.value },
                          })}
                          className="w-full px-2 py-1 rounded-lg bg-surface-hover border border-border text-[10px] text-text-primary focus:outline-none focus:border-accent-primary/50"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

interface Props {
  fields: CheckoutFieldConfig[];
  onChange: (fields: CheckoutFieldConfig[]) => void;
}

export function CheckoutFieldsEditor({ fields, onChange }: Props) {
  const confirmDialog = useConfirmDialog();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [addSection, setAddSection] = useState<'billing' | 'shipping' | 'order' | 'custom'>('custom');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Initialize with defaults if empty
  const effectiveFields = useMemo(() => {
    if (fields.length > 0) return normalizeCheckoutFields(fields);
    return normalizeCheckoutFields(DEFAULT_WC_FIELDS);
  }, [fields]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeField = effectiveFields.find(f => f.id === active.id);
    const overField = effectiveFields.find(f => f.id === over.id);
    if (!activeField || !overField || activeField.section !== overField.section) return;

    const sectionFields = effectiveFields.filter(f => f.section === activeField.section);
    const oldIndex = sectionFields.findIndex(f => f.id === active.id);
    const newIndex = sectionFields.findIndex(f => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedSectionFields = arrayMove(sectionFields, oldIndex, newIndex);
    const knownSections = new Set(CHECKOUT_SECTION_ORDER);
    const sectionSequence = [
      ...CHECKOUT_SECTION_ORDER,
      ...effectiveFields
        .map(f => f.section)
        .filter((section, index, sections) => !knownSections.has(section as any) && sections.indexOf(section) === index),
    ];

    onChange(normalizeCheckoutFields(sectionSequence.flatMap(section => (
      section === activeField.section
        ? reorderedSectionFields
        : effectiveFields.filter(f => f.section === section)
    ))));
  }, [effectiveFields, onChange]);

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  const handleUpdate = useCallback((id: string, updates: Partial<CheckoutFieldConfig>) => {
    onChange(normalizeCheckoutFields(effectiveFields.map(f => f.id === id ? { ...f, ...updates } : f)));
  }, [effectiveFields, onChange]);

  const handleRemove = useCallback((id: string) => {
    onChange(effectiveFields.filter(f => f.id !== id));
    if (expandedId === id) setExpandedId(null);
  }, [effectiveFields, onChange, expandedId]);

  const handleAddField = useCallback(() => {
    const newField: CheckoutFieldConfig = {
      id: `custom_${Date.now()}`,
      label: 'Nowe pole',
      type: 'text',
      section: addSection,
      required: false,
      enabled: true,
      isCustom: true,
      widthDesktop: 'full',
      widthMobile: 'full',
    };
    onChange([...effectiveFields, newField]);
    setExpandedId(newField.id);
  }, [effectiveFields, onChange, addSection]);

  const handleAddHeading = useCallback(() => {
    const newField: CheckoutFieldConfig = {
      id: `heading_${Date.now()}`,
      label: 'Nowy nagłówek',
      type: 'heading',
      section: addSection,
      required: false,
      enabled: true,
      isCustom: true,
    };
    onChange([...effectiveFields, newField]);
    setExpandedId(newField.id);
  }, [effectiveFields, onChange, addSection]);

  const handleResetDefaults = useCallback(async () => {
    if (await confirmDialog({
      title: 'Przywróć domyślne pola',
      message: 'Przywrócić domyślne pola WooCommerce? Niestandardowe pola zostaną usunięte.',
      confirmLabel: 'Przywróć',
    })) {
      onChange(normalizeCheckoutFields([...DEFAULT_WC_FIELDS]));
      setExpandedId(null);
    }
  }, [onChange, confirmDialog]);

  // Stats
  const enabledCount = effectiveFields.filter(f => f.enabled && f.type !== 'heading').length;
  const totalCount = effectiveFields.filter(f => f.type !== 'heading').length;

  // Group fields by section for visual separation
  const groupedFields = useMemo(() => {
    const knownSections = new Set(CHECKOUT_SECTION_ORDER);
    const sectionSequence = [
      ...CHECKOUT_SECTION_ORDER,
      ...effectiveFields
        .map(f => f.section)
        .filter((section, index, sections) => !knownSections.has(section as any) && sections.indexOf(section) === index),
    ];

    return sectionSequence
      .map(section => ({
        section,
        fields: effectiveFields.filter(field => field.section === section),
      }))
      .filter(group => group.fields.length > 0);
  }, [effectiveFields]);

  return (
    <div className="rounded-lg border border-border-subtle overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-surface-hover/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isOpen
            ? <ChevronDown className="w-3.5 h-3.5 text-text-secondary" />
            : <ChevronRight className="w-3.5 h-3.5 text-text-secondary" />}
          <span className="text-xs font-medium text-text-secondary">Pola formularza zamówienia</span>
          <span className="text-[10px] text-text-muted">
            ({enabledCount}/{totalCount} widocznych)
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-border-subtle p-3 space-y-3">
          {/* Reset button */}
          <div className="flex justify-end">
            <button
              onClick={handleResetDefaults}
              className="text-[10px] text-text-muted hover:text-text-secondary transition-colors"
            >
              Przywróć domyślne
            </button>
          </div>

          {/* Field list with DnD, grouped by section */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="space-y-4">
              {groupedFields.map(group => {
                const allEnabled = group.fields.length > 0 && group.fields.every(f => f.enabled);
                const sectionFields = group.fields.filter(f => f.type !== 'heading');
                const toggleSection = () => {
                  const newEnabled = !allEnabled;
                  const updated = effectiveFields.map(f =>
                    f.section === group.section && group.fields.includes(f)
                      ? normalizeCheckoutField({ ...f, enabled: newEnabled })
                      : f
                  );
                  onChange(normalizeCheckoutFields(updated));
                };

                return (
                  <div key={group.section}>
                    {/* Section divider */}
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${SECTION_COLORS[group.section]}`}>
                        {SECTION_LABELS[group.section]}
                      </span>
                      {sectionFields.length > 0 && (
                        <button
                          onClick={toggleSection}
                          className={`text-[9px] px-1.5 py-0.5 rounded-full transition-colors ${
                            allEnabled
                              ? 'bg-success/10 text-success hover:bg-success/20'
                              : 'bg-danger/10 text-danger hover:bg-danger/20'
                          }`}
                        >
                          {allEnabled ? 'Włączona' : 'Wyłączona'}
                        </button>
                      )}
                      <div className="flex-1 h-px bg-surface-hover/50" />
                    </div>
                    <SortableContext
                      items={group.fields.map(field => field.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-1">
                        {group.fields.map(field => (
                          <SortableFieldItem
                            key={field.id}
                            field={field}
                            allFields={effectiveFields}
                            expandedId={expandedId}
                            onToggleExpand={handleToggleExpand}
                            onUpdate={handleUpdate}
                            onRemove={handleRemove}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </div>
                );
              })}
            </div>
          </DndContext>

          {/* Add field controls */}
          <div className="p-3 rounded-lg border border-dashed border-border space-y-2">
            <div className="flex items-center gap-2">
              <select
                value={addSection}
                onChange={e => setAddSection(e.target.value as typeof addSection)}
                className="px-2.5 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-primary"
              >
                {CHECKOUT_SECTION_ORDER.map(s => (
                  <option key={s} value={s}>{SECTION_LABELS[s]}</option>
                ))}
              </select>
              <button
                onClick={handleAddField}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/20 transition-colors"
              >
                <Plus className="w-3 h-3" /> Dodaj pole
              </button>
              <button
                onClick={handleAddHeading}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
              >
                <Settings2 className="w-3 h-3" /> Nagłówek
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
